/**
 * @fileoverview Task Tracker page — assessment/deliverable matrix per class+project.
 * Data from `useTaskTracker`, overlay state from `useTaskTrackerDrawers`, overlays
 * rendered by `TaskTrackerOverlays`; this component only composes.
 */

import { useEffect, useMemo } from 'react'
import { Stack } from '@mantine/core'
import { IconX } from '@tabler/icons-react'
import { useTranslation } from 'react-i18next'
import { Layout } from 'components/Layout'
import { ArcPageShell } from 'components/ArcPageShell'
import { CurrentUserContextProvider, useCurrentUser } from 'context/useCurrentUser'
import { notifications, NotificationProvider } from 'context'
import {
  TaskTrackerFilters,
  TaskTrackerSummary,
  TaskTrackerTable,
  TaskTrackerEmptyState,
  TaskTrackerLoadingState,
} from './components'
import { TaskTrackerOverlays } from './components/TaskTrackerOverlays'
import { useTaskTracker } from './hooks/useTaskTracker'
import { useTaskTrackerDrawers } from './hooks/useTaskTrackerDrawers'
import { isProjectCompleted } from './projectStatus'
import { countTeacherUploads } from './service/taskTrackerService'

function TaskTrackerView() {
  const { t } = useTranslation()
  const { currentUser } = useCurrentUser()
  const tracker = useTaskTracker()
  const { groups, projects, students, matrix, loading, error, filters } = tracker

  const selectedProject = useMemo(
    () => projects.find(p => p.id === filters.projectId),
    [projects, filters.projectId],
  )
  const projectName = selectedProject?.name ?? ''
  const projectFinished = isProjectCompleted(selectedProject?.status)
  const drawers = useTaskTrackerDrawers({ matrix, projectId: filters.projectId, projectName })

  useEffect(() => {
    if (!error) return
    notifications.show({ message: t('task_tracker.error.load_failed'), color: 'red', icon: <IconX size={18} /> })
  }, [error, t])

  const emptyVariant = useMemo(() => {
    if (!filters.groupId) return 'select_class' as const
    if (!filters.projectId) return 'select_project' as const
    if (matrix) {
      const hasTasks = matrix.activities.length > 0 || matrix.finalAssessment.present
      if (!hasTasks) return 'no_tasks' as const
      if (matrix.students.length === 0) return 'no_students' as const
    }
    return null
  }, [filters.groupId, filters.projectId, matrix])

  const showTable = matrix && !emptyVariant && matrix.students.length > 0

  return (
    <Layout>
      <ArcPageShell title={t('task_tracker.title')} subtitle={t('task_tracker.description')}>
        <Stack spacing="lg">
          <TaskTrackerFilters
            groups={groups}
            projects={projects}
            students={students}
            selectedGroupId={filters.groupId}
            selectedProjectId={filters.projectId}
            selectedStudentIds={filters.studentIds}
            onGroupChange={tracker.setGroupId}
            onProjectChange={tracker.setProjectId}
            onStudentChange={tracker.setStudentIds}
            disabled={loading}
          />

          {matrix && !emptyVariant && (
            <TaskTrackerSummary
              summary={matrix.summary}
              projectId={filters.projectId}
              projectName={projectName}
              projectCompleted={projectFinished}
              uploadedByTeacherCount={countTeacherUploads(matrix)}
            />
          )}

          {loading ? (
            <TaskTrackerLoadingState />
          ) : emptyVariant ? (
            <TaskTrackerEmptyState variant={emptyVariant} />
          ) : showTable ? (
            <TaskTrackerTable
              activities={matrix.activities}
              students={matrix.students}
              onTeacherAssessmentClick={drawers.openAssessment}
              onDeliverableCellClick={drawers.openDeliverable}
              onFinalAssessmentClick={drawers.openFinal}
              onUploadForClassClick={projectFinished ? undefined : drawers.openUploadForClass}
              currentUserId={currentUser?.id}
            />
          ) : null}
        </Stack>
      </ArcPageShell>

      <TaskTrackerOverlays
        drawers={drawers}
        projectId={filters.projectId}
        projectName={projectName}
        projectFinished={projectFinished}
        onRefresh={tracker.refresh}
        onRefreshSilent={tracker.refreshSilent}
      />
    </Layout>
  )
}

export function TaskTrackerPage() {
  return (
    <CurrentUserContextProvider>
      <NotificationProvider>
        <TaskTrackerView />
      </NotificationProvider>
    </CurrentUserContextProvider>
  )
}
